import React from 'react';
import { Trophy, Sparkles, PartyPopper, Star } from 'lucide-react';
import { Button } from '../ui/button';

const GoalCelebrationBanner = ({
  goalAmount,
  amountRaised,
  donorCount,
  teamPageTitle,
  onShare,
  variant = 'full' // 'full', 'compact', 'inline'
}) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const overfunded = amountRaised > goalAmount;
  const overfundedPercent = goalAmount > 0 ? Math.round((amountRaised / goalAmount) * 100) : 0;

  if (variant === 'inline') {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border border-yellow-500/30 rounded-full" data-testid="goal-celebration-inline">
        <Trophy className="w-4 h-4 text-yellow-400" />
        <span className="text-sm font-medium text-yellow-400">Goal Reached!</span>
        {overfunded && (
          <span className="text-xs text-amber-400">({overfundedPercent}%)</span>
        )}
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className="bg-gradient-to-r from-yellow-500/10 to-amber-500/10 border border-yellow-500/30 rounded-xl p-4" data-testid="goal-celebration-compact">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
            <Trophy className="w-5 h-5 text-yellow-400" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-yellow-400">Goal Reached!</p>
            <p className="text-sm text-muted-foreground">
              {formatCurrency(amountRaised)} raised of {formatCurrency(goalAmount)} goal
            </p>
          </div>
          {overfunded && (
            <div className="text-right">
              <span className="text-lg font-bold text-amber-400">{overfundedPercent}%</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Full variant
  return (
    <div className="relative overflow-hidden bg-gradient-to-r from-yellow-500/10 via-amber-500/10 to-orange-500/10 border border-yellow-500/30 rounded-2xl p-6" data-testid="goal-celebration-full">
      {/* Decorative elements */}
      <div className="absolute top-2 right-2 opacity-20">
        <PartyPopper className="w-16 h-16 text-yellow-400" />
      </div>
      <div className="absolute bottom-2 left-2 opacity-20">
        <Sparkles className="w-12 h-12 text-amber-400" />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center">
        {/* Icon */}
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-yellow-500/30">
          <Trophy className="w-8 h-8 text-white" />
        </div>

        {/* Title */}
        <h3 className="text-2xl font-bold text-foreground mb-2 flex items-center justify-center gap-2">
          <Star className="w-5 h-5 text-yellow-400" />
          Goal Reached!
          <Star className="w-5 h-5 text-yellow-400" />
        </h3>

        {/* Stats */}
        <p className="text-muted-foreground mb-4">
          {teamPageTitle && <span className="font-medium text-foreground">{teamPageTitle}</span>}
          {teamPageTitle && ' has '} raised{' '}
          <span className="font-bold text-emerald-400">{formatCurrency(amountRaised)}</span>
          {overfunded ? (
            <span>
              {' '}— <span className="text-amber-400">{overfundedPercent}%</span> of the{' '}
              {formatCurrency(goalAmount)} goal!
            </span>
          ) : (
            <span> reaching the {formatCurrency(goalAmount)} goal!</span>
          )}
        </p>

        {/* Donor count */}
        {donorCount > 0 && (
          <p className="text-sm text-muted-foreground mb-4">
            Thanks to <span className="font-medium text-foreground">{donorCount}</span> amazing{' '}
            {donorCount === 1 ? 'donor' : 'donors'}
          </p>
        )}

        {/* Share button */}
        {onShare && (
          <Button
            onClick={onShare}
            className="bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-white"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Share This Achievement
          </Button>
        )}
      </div>
    </div>
  );
};

export default GoalCelebrationBanner;
